import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calculator, ChevronRight, CalendarDays, Phone, Info, CheckCircle2, ArrowRight, AlertTriangle, Users, Home, Heart, ShoppingBag, HandHelping, Smile } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";

const BUNDESLAENDER = [
  "Baden-Württemberg", "Bayern", "Berlin", "Brandenburg", "Bremen", "Hamburg",
  "Hessen", "Mecklenburg-Vorpommern", "Niedersachsen", "Nordrhein-Westfalen",
  "Rheinland-Pfalz", "Saarland", "Sachsen", "Sachsen-Anhalt", "Schleswig-Holstein", "Thüringen"
];

const PFLEGEGRADE = [
  { value: "0", label: "Kein Pflegegrad" },
  { value: "1", label: "Pflegegrad 1" },
  { value: "2", label: "Pflegegrad 2" },
  { value: "3", label: "Pflegegrad 3" },
  { value: "4", label: "Pflegegrad 4" },
  { value: "5", label: "Pflegegrad 5" },
];

const WOHNSITUATIONEN = [
  { value: "allein", label: "Alleinlebend", icon: Home },
  { value: "angehoerige", label: "Mit Angehörigen", icon: Users },
  { value: "betreut", label: "Betreutes Wohnen", icon: Heart },
  { value: "sonstige", label: "Sonstige", icon: Home },
];

const UNTERSTUETZUNG_OPTIONEN = [
  { value: "betreuung", label: "Betreuung", icon: Heart, desc: "Stundenweise Begleitung zuhause" },
  { value: "begleitung", label: "Begleitung", icon: Users, desc: "Zu Terminen und Aktivitäten" },
  { value: "einkaufen", label: "Einkaufen", icon: ShoppingBag, desc: "Hilfe beim Wocheneinkauf" },
  { value: "haushalt", label: "Haushalt", icon: Home, desc: "Unterstützung im Haushalt" },
  { value: "teilhabe", label: "Soziale Teilhabe", icon: Smile, desc: "Gesellschaft und Kontakte" },
  { value: "sonstiges", label: "Weitere Hilfen", icon: HandHelping, desc: "Individuelle Unterstützung" },
];

const BESTEHENDE_LEISTUNGEN = {
  "0": [],
  "1": [
    { name: "Entlastungsbetrag", betrag: "131 € / Monat", info: "Für anerkannte Entlastungsangebote im Alltag" },
    { name: "Pflegeberatung", betrag: "Kostenfrei", info: "Individuelle Beratung nach § 7a SGB XI" },
    { name: "Pflegehilfsmittel", betrag: "Bis 40 € / Monat", info: "Verbrauchsmaterialien für die Pflege" },
    { name: "Wohnraumanpassung", betrag: "Bis 4.180 €", info: "Zuschuss für barrierefreien Umbau" },
  ],
  "2": [
    { name: "Pflegegeld", betrag: "347 € / Monat", info: "Bei häuslicher Pflege durch Angehörige" },
    { name: "Entlastungsbetrag", betrag: "131 € / Monat", info: "Für anerkannte Entlastungsangebote" },
    { name: "Verhinderungs-/Kurzzeitpflege", betrag: "Bis 3.539 € / Jahr", info: "Gemeinsames Jahresbudget VP + KZP" },
    { name: "Kurzzeitpflege", betrag: "Bis 3.539 € / Jahr", info: "Im gemeinsamen VP/KZP-Budget enthalten" },
    { name: "Tages-/Nachtpflege", betrag: "Bis 796 € / Monat", info: "Teilstationäre Betreuung" },
    { name: "Pflegeberatung", betrag: "Kostenfrei", info: "Beratungsbesuch nach § 37 Abs. 3 SGB XI" },
  ],
  "3": [
    { name: "Pflegegeld", betrag: "599 € / Monat", info: "Bei häuslicher Pflege durch Angehörige" },
    { name: "Entlastungsbetrag", betrag: "131 € / Monat", info: "Für anerkannte Entlastungsangebote" },
    { name: "Verhinderungs-/Kurzzeitpflege", betrag: "Bis 3.539 € / Jahr", info: "Gemeinsames Jahresbudget VP + KZP" },
    { name: "Kurzzeitpflege", betrag: "Bis 3.539 € / Jahr", info: "Im gemeinsamen VP/KZP-Budget enthalten" },
    { name: "Tages-/Nachtpflege", betrag: "Bis 1.497 € / Monat", info: "Teilstationäre Betreuung" },
    { name: "Pflegeberatung", betrag: "Kostenfrei", info: "Beratungsbesuch nach § 37 Abs. 3 SGB XI" },
  ],
  "4": [
    { name: "Pflegegeld", betrag: "800 € / Monat", info: "Bei häuslicher Pflege durch Angehörige" },
    { name: "Entlastungsbetrag", betrag: "131 € / Monat", info: "Für anerkannte Entlastungsangebote" },
    { name: "Verhinderungs-/Kurzzeitpflege", betrag: "Bis 3.539 € / Jahr", info: "Gemeinsames Jahresbudget VP + KZP" },
    { name: "Kurzzeitpflege", betrag: "Bis 3.539 € / Jahr", info: "Im gemeinsamen VP/KZP-Budget enthalten" },
    { name: "Tages-/Nachtpflege", betrag: "Bis 1.859 € / Monat", info: "Teilstationäre Betreuung" },
    { name: "Pflegeberatung", betrag: "Kostenfrei", info: "Beratungsbesuch nach § 37 Abs. 3 SGB XI" },
  ],
  "5": [
    { name: "Pflegegeld", betrag: "990 € / Monat", info: "Bei häuslicher Pflege durch Angehörige" },
    { name: "Entlastungsbetrag", betrag: "131 € / Monat", info: "Für anerkannte Entlastungsangebote" },
    { name: "Verhinderungs-/Kurzzeitpflege", betrag: "Bis 3.539 € / Jahr", info: "Gemeinsames Jahresbudget VP + KZP" },
    { name: "Kurzzeitpflege", betrag: "Bis 3.539 € / Jahr", info: "Im gemeinsamen VP/KZP-Budget enthalten" },
    { name: "Tages-/Nachtpflege", betrag: "Bis 2.299 € / Monat", info: "Teilstationäre Betreuung" },
    { name: "Pflegeberatung", betrag: "Kostenfrei", info: "Beratungsbesuch nach § 37 Abs. 3 SGB XI" },
  ],
};

function computeScenario(form) {
  const pg = parseInt(form.pflegegrad, 10);
  const bedarfMap = { gering: 1, mittel: 2, hoch: 3 };
  const bedarfScore = bedarfMap[form.bedarf] || 1;
  const supportCount = form.unterstuetzung.length;
  const isAlone = form.wohnsituation === "allein";
  const hasAngehoerige = form.pflegeDurchAngehoerige === "ja";

  let budgetMin = 0;
  let budgetMax = 0;
  if (pg >= 1) {
    budgetMin = 80 + pg * 40 + bedarfScore * 30 + supportCount * 15;
    budgetMax = budgetMin + 80 + pg * 25;
    if (isAlone) { budgetMin += 30; budgetMax += 50; }
    if (hasAngehoerige) { budgetMax += 40; }
  }

  const leistungsbereiche = [];
  if (form.unterstuetzung.includes("betreuung")) leistungsbereiche.push("Stundenweise Betreuung im häuslichen Umfeld");
  if (form.unterstuetzung.includes("begleitung")) leistungsbereiche.push("Begleitung zu Arztterminen, Therapien und Behörden");
  if (form.unterstuetzung.includes("einkaufen")) leistungsbereiche.push("Unterstützung beim Wocheneinkauf und bei Besorgungen");
  if (form.unterstuetzung.includes("haushalt")) leistungsbereiche.push("Hilfe im Haushalt und bei der Alltagsorganisation");
  if (form.unterstuetzung.includes("teilhabe")) leistungsbereiche.push("Förderung sozialer Kontakte und Teilhabe am Gemeindeleben");
  if (form.unterstuetzung.includes("sonstiges")) leistungsbereiche.push("Individuelle Unterstützungsangebote im Wohnumfeld");

  let bedarfText = "geringer";
  if (bedarfScore === 2) bedarfText = "mittlerer";
  if (bedarfScore === 3) bedarfText = "hoher";

  const entlastung = [];
  if (hasAngehoerige) entlastung.push("Regelmäßige Auszeiten für die pflegenden Angehörigen durch alltagsnahe Hilfen");
  if (isAlone) entlastung.push("Strukturierung des Alltags und Bekämpfung sozialer Isolation");
  entlastung.push("Flexible Nutzung wohnortnaher Unterstützungsangebote");
  entlastung.push("Niedrigschwelliger Zugang ohne bürokratische Hürden");

  return { budgetMin, budgetMax, leistungsbereiche, bedarfText, entlastung, pg };
}

const FAQ_ITEMS = [
  { q: "Was ist das Sozialraumbudget?", a: "Das Sozialraumbudget ist ein politisch diskutiertes Konzept, das Pflegebedürftigen ein flexibles Budget für alltagsnahe Unterstützung im Wohnumfeld zur Verfügung stellen könnte. Die endgültige gesetzliche Ausgestaltung steht noch aus." },
  { q: "Ist das Sozialraumbudget bereits beschlossen?", a: "Nein, das Sozialraumbudget befindet sich im Diskussions- und Planungsstadium. Es gibt noch keinen verbindlichen Gesetzesbeschluss. Dieser Rechner dient ausschließlich der Information und Orientierung." },
  { q: "Wer könnte vom Sozialraumbudget profitieren?", a: "Voraussichtlich Pflegebedürftige mit einem anerkannten Pflegegrad, die zuhause leben. Die genauen Anspruchsvoraussetzungen hängen von der endgültigen gesetzlichen Regelung ab." },
  { q: "Wie hoch könnte das Sozialraumbudget sein?", a: "Konkrete Beträge sind zum jetzigen Zeitpunkt noch nicht gesetzlich festgelegt. Die in diesem Rechner gezeigten Szenarien basieren auf aktuellen Diskussionsansätzen und dienen ausschließlich der Orientierung." },
  { q: "Ist das Ergebnis dieses Rechners verbindlich?", a: "Nein. Die Ergebnisse stellen ausdrücklich keinen Leistungsanspruch dar. Es handelt sich um eine unverbindliche Simulation möglicher Reformansätze auf Basis des aktuellen Diskussionsstands." },
  { q: "Kann ich bereits Leistungen beantragen?", a: "Das Sozialraumbudget kann derzeit nicht beantragt werden, da es noch nicht eingeführt ist. Sie können aber Ihre bestehenden Pflegeleistungen prüfen und optimieren lassen — unsere Pflegeberatung hilft Ihnen dabei." },
  { q: "Welche Leistungen gibt es heute schon?", a: "Bereits heute stehen Pflegebedürftigen je nach Pflegegrad verschiedene Leistungen zu: Pflegegeld, Entlastungsbetrag (131 €/Monat), Verhinderungspflege (bis 3.539 €/Jahr), Kurzzeitpflege, Tages-/Nachtpflege, Pflegehilfsmittel und kostenfreie Pflegeberatung." },
];

export default function SozialraumbudgetRechner() {
  const [form, setForm] = useState({
    alter: "",
    bundesland: "Nordrhein-Westfalen",
    pflegegrad: "",
    wohnsituation: "",
    pflegeDurchAngehoerige: "",
    bedarf: "",
    unterstuetzung: [],
  });
  const [showResult, setShowResult] = useState(false);
  const [scenario, setScenario] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Sozialraumbudget-Rechner | Unverbindliche Simulation | Lehnen & Weinacht";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "Sozialraumbudget-Rechner: Unverbindliche Simulation möglicher Reformansätze. Erfahren Sie, welche Unterstützung künftig möglich sein könnte — und welche Leistungen Ihnen bereits heute zustehen.");
  }, []);

  const toggleUnterstuetzung = (val) => {
    setForm(prev => ({
      ...prev,
      unterstuetzung: prev.unterstuetzung.includes(val)
        ? prev.unterstuetzung.filter(v => v !== val)
        : [...prev.unterstuetzung, val]
    }));
  };

  const isFormValid = form.pflegegrad !== "" && form.wohnsituation !== "" && form.pflegeDurchAngehoerige !== "" && form.bedarf !== "" && form.unterstuetzung.length > 0;

  const handleCalculate = () => {
    if (!isFormValid) return;
    const result = computeScenario(form);
    setScenario(result);
    setShowResult(true);
    setTimeout(() => {
      document.getElementById("ergebnis")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const handleReset = () => {
    setShowResult(false);
    setScenario(null);
    setForm({ alter: "", bundesland: "Nordrhein-Westfalen", pflegegrad: "", wohnsituation: "", pflegeDurchAngehoerige: "", bedarf: "", unterstuetzung: [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="sozialraumbudget-rechner">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="h-9 w-9 shrink-0 overflow-hidden">
              <img src={ICON_URL} alt="Logo" className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%]" />
            </div>
            <div>
              <span className="text-sm font-bold text-slate-800 font-['Manrope'] block leading-tight">Lehnen & Weinacht</span>
              <span className="text-[9px] text-cyan-600 font-semibold tracking-wider uppercase">Pflegeberatung & Alltagsbegleitung</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm text-slate-500 hover:text-cyan-700 transition-colors hidden sm:inline">Ratgeber</Link>
            <a href="/#termin-buchen" className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-all active:scale-95">
              Termin buchen
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-cyan-700 transition-colors">Startseite</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-cyan-700 transition-colors">Ratgeber</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-slate-600">Sozialraumbudget-Rechner</span>
        </nav>

        {/* Page title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-700 bg-cyan-50 px-4 py-1.5 rounded-full mb-4">
            <Calculator className="h-4 w-4" />
            Informations- und Szenarienrechner
          </div>
          <h1 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-slate-900 leading-tight mb-3">
            Sozialraumbudget-Rechner
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            Unverbindliche Simulation möglicher Reformansätze
          </p>
        </div>

        {/* Hinweisbox */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 sm:p-6 mb-10" data-testid="rechner-hinweis">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-bold text-amber-800 mb-1">Wichtiger Hinweis</p>
              <p className="text-sm text-amber-700 leading-relaxed">
                Das Sozialraumbudget wird derzeit politisch diskutiert und ist noch nicht endgültig gesetzlich eingeführt.
                Dieser Rechner dient ausschließlich als <strong>Informations- und Szenarienrechner</strong>.
                Die Ergebnisse stellen keinen Leistungsanspruch dar und ersetzen keine individuelle Pflegeberatung.
              </p>
            </div>
          </div>
        </div>

        {/* Calculator form */}
        {!showResult && (
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-lg shadow-cyan-900/5 p-6 sm:p-8 mb-10" data-testid="rechner-form">
            <h2 className="font-['Manrope'] text-xl font-bold text-slate-900 mb-6">Ihre Angaben</h2>

            <div className="space-y-8">
              {/* Row 1: Alter + Bundesland */}
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-base font-semibold text-slate-700 mb-2">Alter</label>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    data-testid="rechner-alter"
                    value={form.alter}
                    onChange={e => setForm({ ...form, alter: e.target.value })}
                    placeholder="z. B. 78"
                    className="w-full h-14 rounded-xl border border-slate-200 bg-white px-4 text-lg focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-base font-semibold text-slate-700 mb-2">Bundesland</label>
                  <select
                    data-testid="rechner-bundesland"
                    value={form.bundesland}
                    onChange={e => setForm({ ...form, bundesland: e.target.value })}
                    className="w-full h-14 rounded-xl border border-slate-200 bg-white px-4 text-lg focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-cyan-400 transition-all"
                  >
                    {BUNDESLAENDER.map(bl => <option key={bl} value={bl}>{bl}</option>)}
                  </select>
                </div>
              </div>

              {/* Pflegegrad */}
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-3">Pflegegrad *</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {PFLEGEGRADE.map(pg => (
                    <button
                      key={pg.value}
                      type="button"
                      data-testid={`rechner-pg-${pg.value}`}
                      onClick={() => setForm({ ...form, pflegegrad: pg.value })}
                      className={`p-4 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${
                        form.pflegegrad === pg.value
                          ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200"
                          : "border-slate-200 hover:border-cyan-200"
                      }`}
                    >
                      <span className="text-base font-bold text-slate-800 block">{pg.value === "0" ? "Kein" : pg.value}</span>
                      {pg.value !== "0" && <span className="text-xs text-slate-500">Pflegegrad</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Wohnsituation */}
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-3">Wohnsituation *</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {WOHNSITUATIONEN.map(ws => {
                    const Icon = ws.icon;
                    return (
                      <button
                        key={ws.value}
                        type="button"
                        data-testid={`rechner-wohn-${ws.value}`}
                        onClick={() => setForm({ ...form, wohnsituation: ws.value })}
                        className={`p-4 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${
                          form.wohnsituation === ws.value
                            ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200"
                            : "border-slate-200 hover:border-cyan-200"
                        }`}
                      >
                        <Icon className="h-6 w-6 mx-auto mb-2 text-cyan-700 opacity-70" />
                        <span className="text-sm font-bold text-slate-800">{ws.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Pflege durch Angehörige */}
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-3">Pflege durch Angehörige? *</label>
                <div className="grid grid-cols-2 gap-3 max-w-sm">
                  {[{ value: "ja", label: "Ja" }, { value: "nein", label: "Nein" }].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`rechner-angehoerige-${opt.value}`}
                      onClick={() => setForm({ ...form, pflegeDurchAngehoerige: opt.value })}
                      className={`p-4 rounded-xl border-2 text-center transition-all active:scale-[0.97] text-lg font-bold ${
                        form.pflegeDurchAngehoerige === opt.value
                          ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200 text-cyan-700"
                          : "border-slate-200 hover:border-cyan-200 text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Unterstützungsbedarf */}
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-3">Unterstützungsbedarf im Alltag *</label>
                <div className="grid grid-cols-3 gap-3 max-w-md">
                  {[{ value: "gering", label: "Gering" }, { value: "mittel", label: "Mittel" }, { value: "hoch", label: "Hoch" }].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      data-testid={`rechner-bedarf-${opt.value}`}
                      onClick={() => setForm({ ...form, bedarf: opt.value })}
                      className={`p-4 rounded-xl border-2 text-center transition-all active:scale-[0.97] ${
                        form.bedarf === opt.value
                          ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200"
                          : "border-slate-200 hover:border-cyan-200"
                      }`}
                    >
                      <span className="text-base font-bold text-slate-800">{opt.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Gewünschte Unterstützung */}
              <div>
                <label className="block text-base font-semibold text-slate-700 mb-1">Gewünschte Unterstützung *</label>
                <p className="text-sm text-slate-500 mb-3">Mehrere Auswahlmöglichkeiten</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {UNTERSTUETZUNG_OPTIONEN.map(opt => {
                    const Icon = opt.icon;
                    const selected = form.unterstuetzung.includes(opt.value);
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`rechner-support-${opt.value}`}
                        onClick={() => toggleUnterstuetzung(opt.value)}
                        className={`p-4 rounded-xl border-2 text-left transition-all active:scale-[0.97] ${
                          selected
                            ? "border-cyan-600 bg-cyan-50 ring-1 ring-cyan-200"
                            : "border-slate-200 hover:border-cyan-200"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <Icon className={`h-5 w-5 ${selected ? "text-cyan-700" : "text-slate-400"}`} />
                          <span className="text-sm font-bold text-slate-800">{opt.label}</span>
                        </div>
                        <span className="text-xs text-slate-500">{opt.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Submit */}
              <div className="pt-2">
                <Button
                  data-testid="rechner-berechnen-btn"
                  onClick={handleCalculate}
                  disabled={!isFormValid}
                  className="w-full sm:w-auto bg-cyan-700 hover:bg-cyan-600 disabled:bg-slate-300 text-white rounded-xl h-14 px-10 text-lg font-semibold shadow-md hover:shadow-lg transition-all"
                >
                  <Calculator className="h-5 w-5 mr-2" />
                  Szenario berechnen
                </Button>
                {!isFormValid && (
                  <p className="text-sm text-slate-400 mt-3">Bitte füllen Sie alle mit * markierten Felder aus.</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Result */}
        {showResult && scenario && (
          <div id="ergebnis" data-testid="rechner-ergebnis">
            {/* Scenario heading */}
            <div className="bg-white rounded-2xl border border-cyan-200 shadow-lg shadow-cyan-900/5 p-6 sm:p-8 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-10 w-10 bg-cyan-100 rounded-xl flex items-center justify-center">
                  <Calculator className="h-5 w-5 text-cyan-700" />
                </div>
                <div>
                  <h2 className="font-['Manrope'] text-xl font-bold text-slate-900">Mögliches Szenario</h2>
                  <p className="text-xs text-slate-500">Auf Basis aktueller Reformdiskussionen</p>
                </div>
              </div>

              <div className="bg-slate-50 rounded-xl p-5 mb-5 border border-slate-100">
                <p className="text-base text-slate-700 leading-relaxed">
                  Nach aktuellen Diskussionen rund um zukünftige Pflegeleistungen könnte für Ihre Situation künftig ein Unterstützungsbudget in Betracht kommen.
                </p>
              </div>

              {scenario.pg >= 1 && (
                <div className="bg-gradient-to-br from-cyan-50 to-green-50 rounded-xl p-5 mb-6 border border-cyan-100">
                  <p className="text-sm font-semibold text-cyan-800 mb-1">Möglicher Orientierungsrahmen</p>
                  <p className="text-3xl sm:text-4xl font-extrabold text-cyan-800 font-['Manrope']">
                    {scenario.budgetMin}–{scenario.budgetMax} € <span className="text-lg font-semibold text-cyan-600">/ Monat</span>
                  </p>
                  <p className="text-xs text-cyan-600 mt-2">
                    Unverbindliche Simulation — kein Leistungsanspruch. Basiert auf aktuellem Diskussionsstand.
                  </p>
                </div>
              )}

              {scenario.pg === 0 && (
                <div className="bg-amber-50 rounded-xl p-5 mb-6 border border-amber-200">
                  <p className="text-sm text-amber-800 leading-relaxed">
                    <strong>Ohne anerkannten Pflegegrad</strong> stehen derzeit keine Pflegeleistungen zur Verfügung. Sollte ein Unterstützungsbedarf bestehen, empfehlen wir die Beantragung eines Pflegegrads. Wir beraten Sie gerne dazu.
                  </p>
                </div>
              )}

              {/* Unterstützungsbedarf */}
              <div className="mb-6">
                <h3 className="text-base font-bold text-slate-800 mb-3">Möglicher Unterstützungsbedarf</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Auf Basis Ihrer Angaben besteht voraussichtlich ein <strong>{scenario.bedarfText} Unterstützungsbedarf</strong> im Alltag.
                </p>
              </div>

              {/* Leistungsbereiche */}
              {scenario.leistungsbereiche.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-base font-bold text-slate-800 mb-3">Mögliche Leistungsbereiche</h3>
                  <ul className="space-y-2">
                    {scenario.leistungsbereiche.map((lb, i) => (
                      <li key={i} className="flex items-start gap-2.5">
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                        <span className="text-sm text-slate-600">{lb}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Entlastung */}
              <div className="mb-4">
                <h3 className="text-base font-bold text-slate-800 mb-3">Mögliche Entlastungsangebote</h3>
                <ul className="space-y-2">
                  {scenario.entlastung.map((e, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CheckCircle2 className="h-5 w-5 text-cyan-600 mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-600">{e}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Bestehende Leistungen */}
            {(BESTEHENDE_LEISTUNGEN[form.pflegegrad] || []).length > 0 && (
              <div className="bg-white rounded-2xl border border-green-200 shadow-lg shadow-green-900/5 p-6 sm:p-8 mb-6" data-testid="bestehende-leistungen">
                <div className="flex items-center gap-2 mb-5">
                  <div className="h-10 w-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <CheckCircle2 className="h-5 w-5 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-['Manrope'] text-lg font-bold text-slate-900">Bereits heute verfügbare Leistungen</h3>
                    <p className="text-xs text-slate-500">Diese Leistungen stehen Ihnen mit Pflegegrad {form.pflegegrad} bereits zu</p>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  {BESTEHENDE_LEISTUNGEN[form.pflegegrad].map((l, i) => (
                    <div key={i} className="bg-green-50/50 rounded-xl p-4 border border-green-100">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-slate-800">{l.name}</span>
                        <span className="text-sm font-bold text-green-700">{l.betrag}</span>
                      </div>
                      <p className="text-xs text-slate-500">{l.info}</p>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-4 italic">
                  Viele Pflegebedürftige schöpfen ihre bestehenden Ansprüche nicht aus. Unsere Pflegeberatung hilft Ihnen, alle Leistungen optimal zu nutzen.
                </p>
              </div>
            )}

            {/* Hinweis */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-700 leading-relaxed">
                  <strong>Bitte beachten Sie:</strong> Alle Angaben in diesem Szenario sind unverbindlich und basieren auf dem aktuellen Diskussionsstand. Ein gesetzlicher Anspruch auf das Sozialraumbudget besteht derzeit nicht. Für eine individuelle Einschätzung Ihrer Pflegesituation empfehlen wir eine professionelle Pflegeberatung.
                </p>
              </div>
            </div>

            {/* CTA Box */}
            <div className="bg-gradient-to-br from-cyan-50 to-green-50 rounded-2xl border border-cyan-100 p-8 mb-8" data-testid="rechner-cta">
              <h3 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-3">
                Fragen zu Pflegeleistungen oder zukünftigen Reformen?
              </h3>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                Unsere digitale Pflegeberatung unterstützt Pflegebedürftige und Angehörige bei Fragen zu Pflegegrad, Entlastungsleistungen, Beratungsbesuchen nach § 37 Abs. 3 SGB XI und aktuellen Reformvorhaben.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/#termin-buchen" className="inline-flex items-center justify-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]" data-testid="rechner-cta-termin">
                  <CalendarDays className="h-5 w-5" /> Kostenlose Beratung anfragen
                </a>
                <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]">
                  <Phone className="h-5 w-5" /> 0163 791 7044
                </a>
              </div>
            </div>

            {/* Neu berechnen */}
            <div className="text-center mb-10">
              <Button onClick={handleReset} variant="outline" className="rounded-xl px-8 h-12 text-base" data-testid="rechner-reset-btn">
                Neue Berechnung starten
              </Button>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <section className="mb-12" data-testid="rechner-faq">
          <h2 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-6">Häufige Fragen zum Sozialraumbudget</h2>
          <Accordion type="single" collapsible className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200/60 rounded-xl px-5 shadow-sm">
                <AccordionTrigger className="text-base font-semibold text-slate-800 hover:text-cyan-700 hover:no-underline py-4">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-base text-slate-600 leading-relaxed pb-4">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* Verwandte Artikel */}
        <section className="mb-12">
          <h3 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4">Weiterführende Artikel</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { slug: "sozialraumbudget-pflege", title: "Sozialraumbudget in der Pflege: Was Sie wissen sollten", category: "Sozialraumbudget" },
              { slug: "sozialraumbudget-pflegegrad", title: "Sozialraumbudget und Pflegegrad", category: "Pflegegrad" },
              { slug: "sozialraumbudget-2027", title: "Sozialraumbudget 2027: Was ist bisher bekannt?", category: "Pflegereform" },
              { slug: "sozialraumbudget-entlastungsbetrag", title: "Sozialraumbudget oder Entlastungsbetrag?", category: "Entlastungsbetrag" },
            ].map(a => (
              <Link key={a.slug} to={`/blog/${a.slug}`} className="bg-white border border-slate-200/60 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group">
                <span className="text-xs font-medium text-cyan-700 bg-cyan-50 px-2 py-1 rounded-full">{a.category}</span>
                <p className="text-sm font-semibold text-slate-800 mt-2 group-hover:text-cyan-700 transition-colors">{a.title}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Legal disclaimer */}
        <div className="bg-slate-100 rounded-xl p-5 mb-10 text-sm text-slate-500 italic">
          Dieser Rechner dient ausschließlich der allgemeinen Information und stellt keine Rechts- oder Leistungsberatung dar. Die dargestellten Szenarien beruhen auf dem aktuellen Diskussionsstand und können sich jederzeit ändern. Für eine individuelle Einschätzung Ihrer Pflegesituation nutzen Sie bitte unsere kostenfreie Pflegeberatung.
        </div>

        {/* Back to blog */}
        <div className="text-center pt-6 border-t border-slate-200 mb-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-cyan-700 hover:underline font-medium">
            <ArrowRight className="h-4 w-4 rotate-180" /> Alle Ratgeber-Artikel
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung</p>
          <div className="flex justify-center gap-6 mt-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Ratgeber</Link>
            <a href="/#kontakt" className="hover:text-white transition-colors">Kontakt</a>
          </div>
        </div>
      </footer>

      {/* FAQPage structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": FAQ_ITEMS.map(item => ({
              "@type": "Question",
              "name": item.q,
              "acceptedAnswer": { "@type": "Answer", "text": item.a }
            }))
          })
        }}
      />
    </div>
  );
}
